import React, { useEffect, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import API from "../api/axiosInstance";
import { ApprovalQueueAssignment } from "../types/interfaces";
import {
    Box,
    Typography,
    Tabs,
    Tab,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    useTheme
} from "@mui/material";



const flagColors = ["gray", "red", "orange", "yellow", "green", "blue"];

const MyApprovals: React.FC = () => {
    const [tab, setTab] = useState(0);
    const [tasks, setTasks] = useState<ApprovalQueueAssignment[]>([]);
    const gridRef = useRef<AgGridReact>(null);

    const [queueFilter, setQueueFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [priorityFilter, setPriorityFilter] = useState("");
    const [groupByField, setGroupByField] = useState("");

    const theme = useTheme();

    useEffect(() => {
        loadTasks();
    }, []);

    const loadTasks = async () => {
        try {
            const res = await API.get("/approvals/my-assignments");
            setTasks(res.data);
        } catch (err) {
            console.error("Failed to load approvals", err);
        }
    };

    const onRowDoubleClicked = (e: any) => {
        const recordId = e.data.recordID;
        window.location.href = `/approvals/${recordId}`;
    };

    const handleFlagChange = (rowData: ApprovalQueueAssignment) => {
        const next = (flagColors.indexOf(rowData.flagColor || "gray") + 1) % flagColors.length;
        const updated = { ...rowData, flagColor: flagColors[next] };
        setTasks((prev) =>
            prev.map((t) => (t.assignmentID === updated.assignmentID ? updated : t))
        );
    };

    const columns: ColDef<ApprovalQueueAssignment>[] = [
        {
            headerName: "Flag",
            field: "flagColor",
            width: 80,
            cellRenderer: (params: ICellRendererParams<ApprovalQueueAssignment>) =>
                params.data ? (
                    <div
                        onClick={() => handleFlagChange(params.data!)}
                        style={{
                            width: "20px",
                            height: "20px",
                            borderRadius: "50%",
                            backgroundColor: params.value || "gray",
                            cursor: "pointer",
                            margin: "auto",
                        }}
                    />
                ) : null,

        },
        { field: "recordTitle", headerName: "Title", flex: 1 },
        { field: "queueName", headerName: "Queue", flex: 1 },
        { field: "status", headerName: "Status", flex: 1 },
        { field: "priority", headerName: "Priority", flex: 1 },
        { field: "assignedAt", headerName: "Assigned At", flex: 1 },
    ];

    const filteredTasks = tasks.filter((task) => {
        return (
            (!queueFilter || task.queueName === queueFilter) &&
            (!statusFilter || task.status === statusFilter) &&
            (!priorityFilter || task.priority === priorityFilter)
        );
    });

    const unique = <K extends keyof ApprovalQueueAssignment>(key: K) =>
        Array.from(new Set(tasks.map((t) => t[key]).filter(Boolean)));


    return (
        <Box
            sx={{
                padding: 3,
                backgroundColor: theme.palette.background.default,
                color: theme.palette.text.primary,
                minHeight: "100vh",
            }}
        >
            <Typography variant="h5" gutterBottom>
                My Approvals
            </Typography>

            <Tabs
                value={tab}
                onChange={(_, newValue) => setTab(newValue)}
                sx={{ marginBottom: 2 }}
            >
                <Tab label="My Tasks" />
                <Tab label="Recent Items" />
                <Tab label="Options" />
            </Tabs>

            {tab === 0 && (
                <>
                    {/* Filters */}
                    <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
                        <FormControl sx={{ minWidth: 180 }}>
                            <InputLabel>Queue</InputLabel>
                            <Select
                                value={queueFilter}
                                label="Queue"
                                onChange={(e) => setQueueFilter(e.target.value)}
                            >
                                <MenuItem value="">All Queues</MenuItem>
                                {unique("queueName").map((q) => (
                                    <MenuItem key={q} value={q}>{q}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl sx={{ minWidth: 180 }}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={statusFilter}
                                label="Status"
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <MenuItem value="">All Statuses</MenuItem>
                                {unique("status").map((s) => (
                                    <MenuItem key={s} value={s}>{s}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl sx={{ minWidth: 180 }}>
                            <InputLabel>Priority</InputLabel>
                            <Select
                                value={priorityFilter}
                                label="Priority"
                                onChange={(e) => setPriorityFilter(e.target.value)}
                            >
                                <MenuItem value="">All Priorities</MenuItem>
                                {unique("priority").map((p) => (
                                    <MenuItem key={p} value={p}>{p}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl sx={{ minWidth: 180 }}>
                            <InputLabel>Group By</InputLabel>
                            <Select
                                value={groupByField}
                                label="Group By"
                                onChange={(e) => setGroupByField(e.target.value)}
                            >
                                <MenuItem value="">No Grouping</MenuItem>
                                <MenuItem value="queueName">Queue</MenuItem>
                                <MenuItem value="priority">Priority</MenuItem>
                                <MenuItem value="status">Status</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Data Grid */}
                    <Box
                        className={`ag-theme-${theme.palette.mode === 'dark' ? 'alpine-dark' : 'alpine'}`}
                        sx={{ height: 500, width: "100%" }}
                    >
                        <AgGridReact
                            ref={gridRef}
                            rowData={filteredTasks}
                            columnDefs={columns}
                            rowSelection="single"
                            animateRows
                            onRowDoubleClicked={onRowDoubleClicked}
                            groupDisplayType="multipleColumns"
                            groupIncludeFooter
                            gridOptions={{
                                groupDefaultExpanded: 1,
                                groupIncludeTotalFooter: true,
                                groupDisplayType: "singleColumn",
                            }}
                            autoGroupColumnDef={
                                groupByField
                                    ? {
                                        headerName: groupByField,
                                        field: groupByField,
                                        cellRendererParams: { suppressCount: true },
                                    }
                                    : undefined
                            }
                        />
                    </Box>
                </>
            )}

            {tab === 1 && (
                <Typography variant="body1" mt={2}>
                    Recent activity will go here...
                </Typography>
            )}

            {tab === 2 && (
                <Typography variant="body1" mt={2}>
                    User configuration settings will go here...
                </Typography>
            )}
        </Box>
    );
};

export default MyApprovals;
